import { CheckCircle2 } from 'lucide-react';
import type { AuditResponses, AuditSection } from '../../lib/api/queries';

/**
 * Sticky sidebar with the section list. Each row shows the section code +
 * name + a per-section completion percentage so the auditor can see at a
 * glance which sections are done. Selecting a section sets the active
 * section in the parent.
 */
export function SectionSidebar({
  sections,
  responses,
  activeSectionId,
  onSelect,
}: {
  sections: AuditSection[];
  responses: AuditResponses | undefined;
  activeSectionId: string | null;
  onSelect: (sectionId: string) => void;
}) {
  return (
    <nav className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide
                    bg-gray-50 border-b border-gray-200">
        Sections
      </p>
      <ul className="divide-y divide-gray-100">
        {sections.map(s => {
          const pct = sectionCompletion(s, responses);
          const isActive = s.id === activeSectionId;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelect(s.id)}
                className={
                  'w-full text-left px-4 py-2.5 transition-colors flex items-start gap-2 ' +
                  (isActive
                    ? 'bg-accent-alt-50 border-l-4 border-accent-alt-600'
                    : 'border-l-4 border-transparent hover:bg-gray-50')
                }
              >
                <div className="flex-1 min-w-0">
                  <p className={'text-sm font-medium truncate ' +
                    (isActive ? 'text-accent-alt-700' : 'text-gray-800')}>
                    {s.code} {s.name}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <ProgressBar pct={pct} />
                    <span className="text-xs text-gray-500 tabular-nums w-9 text-right">
                      {Math.round(pct * 100)}%
                    </span>
                  </div>
                </div>
                {pct >= 1 && (
                  <CheckCircle2 className="w-4 h-4 text-success-500 mt-0.5 shrink-0" aria-hidden />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ProgressBar({ pct }: { pct: number }) {
  const width = Math.max(0, Math.min(1, pct)) * 100;
  return (
    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-full bg-accent-alt-500 transition-all"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

/**
 * A section is "complete" when every visible evidence item has a score
 * (including -1 N/A). Conditionally-hidden items are excluded from both
 * numerator and denominator. The hidden set comes from the server
 * (`hiddenItemIds`, Slice 19a), the same resolver scoring uses, so
 * percentages can't disagree between views. Returns 0..1.
 */
function sectionCompletion(s: AuditSection, responses: AuditResponses | undefined): number {
  if (!responses) return 0;
  const hiddenItemIds = new Set(responses.hiddenItemIds);

  const visibleItemIds = s.criteria.flatMap(c =>
    c.items
      .filter(i => !hiddenItemIds.has(i.id))
      .map(i => i.id),
  );
  if (visibleItemIds.length === 0) return 1;
  const answered = new Set(
    responses.answers
      .filter(a => a.score !== null)
      .map(a => a.criterionItemId),
  );
  const hits = visibleItemIds.filter(id => answered.has(id)).length;
  return hits / visibleItemIds.length;
}
